import { IconCalendar } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Calendar } from 'app/design/primitives/calendar';
import { FormError } from 'app/design/primitives/form-error';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import { useFormContext } from 'app/hooks/form';
import { localeAwareFormat } from 'app/lib/date';
import { createTheme as createDSTheme } from 'app/shopify';
import type { CreateListingDTO } from 'app/types/listing';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import React, { useState } from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';

const theme = createDSTheme();

type DateField = 'start' | 'end';

function DateButton({ field, label, onPress }: { field: DateField; label: string; onPress: () => void }) {
  const { watch } = useFormContext<CreateListingDTO>();
  const value = watch(field);
  const { t } = useTranslation('new');

  return (
    <Pressable onPress={onPress} style={{ flex: 1 }}>
      {({ pressed }) => (
        <Row
          alignItems="center"
          backgroundColor="surface.default"
          borderRadius="radius.lg"
          gap="spacing.general.x2"
          justifyContent="space-between"
          minHeight={60}
          padding="spacing.general.x3"
          style={{ opacity: pressed ? 0.8 : 1 }}
        >
          <Column flex={1} gap="spacing.general.x1">
            <Text color="text.primary" fontWeight="700" variant="bodySmall">
              {label}
            </Text>
            <Text color={value ? 'text.primary' : 'text.placeholder'} variant="bodySmall">
              {value ? localeAwareFormat(new Date(value), 'P') : t('hauteGaspesie.availability.placeholder')}
            </Text>
          </Column>
          <IconCalendar strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.primary']} size={20} />
        </Row>
      )}
    </Pressable>
  );
}

export function ExpirableAvailabilitySection() {
  const { formState, setValue, trigger, watch } = useFormContext<CreateListingDTO>();
  const { t } = useTranslation('new');
  const start = watch('start');
  const end = watch('end');
  const [activeField, setActiveField] = useState<DateField>();
  const [selectedDate, setSelectedDate] = useState<Date>();

  const openPicker = (field: DateField) => {
    const value = field === 'start' ? start : end;
    setSelectedDate(value ? new Date(value) : undefined);
    setActiveField(field);
  };

  const closePicker = () => {
    setActiveField(undefined);
    setSelectedDate(undefined);
  };

  const saveDate = () => {
    if (!activeField || !selectedDate) return;
    setValue(activeField, selectedDate.toISOString(), { shouldDirty: true, shouldValidate: true });
    closePicker();
    void Promise.all([trigger('start'), trigger('end')]);
  };

  return (
    <Column gap="spacing.general.x2">
      <Text color="text.primary" fontWeight="700" textAlign="center" variant="bodyBase">
        {t('hauteGaspesie.availability.title')}
      </Text>
      <Row gap="spacing.general.x2">
        <DateButton
          field="start"
          label={t('hauteGaspesie.availability.startLabel')}
          onPress={() => openPicker('start')}
        />
        <DateButton field="end" label={t('hauteGaspesie.availability.endLabel')} onPress={() => openPicker('end')} />
      </Row>
      <FormError error={formState.errors.end} />

      <Modal onClose={closePicker} open={Boolean(activeField)} transparent>
        <Modal.Card gap="spacing.general.x4">
          <Modal.Title allowClose onClose={closePicker}>
            {t(
              activeField === 'end'
                ? 'hauteGaspesie.availability.endPickerTitle'
                : 'hauteGaspesie.availability.startPickerTitle',
            )}
          </Modal.Title>
          <Calendar
            initial={selectedDate ?? (activeField === 'end' && start ? new Date(start) : new Date())}
            minCurrent={activeField === 'end' && start ? new Date(start) : undefined}
            onDatePress={setSelectedDate}
            selected={selectedDate ? [selectedDate] : []}
            startFromNow
          />
          <Button disabled={!selectedDate} onPress={saveDate} variant="primary">
            {t('hauteGaspesie.availability.confirm')}
          </Button>
        </Modal.Card>
      </Modal>
    </Column>
  );
}
